import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import { useId, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type SettingsCardProps = {
    title: string;
    children: ReactNode;
    dirty: boolean;
    saving?: boolean;
    onSave: () => void;
    onReset: () => void;
    saveLabel?: string;
    resetLabel?: string;
    disableSave?: boolean;
    readOnly?: boolean;
};

export function SettingsCard({
    title,
    children,
    dirty,
    saving = false,
    onSave,
    onReset,
    saveLabel,
    resetLabel,
    disableSave,
    readOnly = false,
}: SettingsCardProps): ReactElement {
    const { t } = useTranslation();
    const resolvedSaveLabel = saveLabel ?? t('SETTINGS.ACTIONS.SAVE');
    const resolvedResetLabel = resetLabel ?? t('SETTINGS.ACTIONS.RESET');
    const headingId = useId();

    return (
        <Card variant="outlined" component="section" aria-labelledby={headingId}>
            <CardHeader title={title} slotProps={{ title: { variant: 'h6', component: 'h2', id: headingId } }} />
            <CardContent sx={{ pt: { xs: 2, md: 0 }, pb: readOnly ? 2 : undefined }}>{children}</CardContent>
            {readOnly ? undefined : (
                <CardActions sx={{ px: 2, pt: { xs: 2, md: 1 }, pb: 2, gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onSave}
                        disabled={!dirty || saving || disableSave}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
                        {resolvedSaveLabel}
                    </Button>
                    <Button variant="outlined" color="inherit" onClick={onReset} disabled={!dirty || saving}>
                        {resolvedResetLabel}
                    </Button>
                </CardActions>
            )}
        </Card>
    );
}
